// src/layouts/DashboardLayout.js
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Box, CssBaseline, Toolbar, alpha, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import ExecutiveAppBar from '../components/ExecutiveAppBar';
import ExecutiveSidebar from '../components/ExecutiveSidebar';
import FloatingActionButton from '../components/FloatingActionButton';
import MobileBottomNav from '../components/MobileBottomNav';
import { isNativeMobilePlatform } from '../utils/platform';

const NativeDashboardLayout = lazy(() => import('./NativeDashboardLayout'));

const EXPANDED_DRAWER_WIDTH = 204;
const COLLAPSED_DRAWER_WIDTH = 64;
const SIDEBAR_STORAGE_KEY = 'sidebarCollapsed';

export default function DashboardLayout({ darkMode, setDarkMode }) {
    const nativeMobile = isNativeMobilePlatform();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => (
        localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'
    ));
    const mainRef = useRef(null);
    const location = useLocation();
    const drawerWidth = isDesktop
        ? (isSidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : EXPANDED_DRAWER_WIDTH)
        : EXPANDED_DRAWER_WIDTH;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleSidebarCollapseToggle = () => {
        setIsSidebarCollapsed((prev) => !prev);
    };

    useEffect(() => {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, isSidebarCollapsed ? '1' : '0');
    }, [isSidebarCollapsed]);

    useEffect(() => {
        setMobileOpen(false);
        if (!nativeMobile) {
            window.requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }));
        }
    }, [location.pathname, nativeMobile]);

    // Detectamos si estamos en la vista de chat para ajustar el layout
    const isChat = location.pathname === '/chat';

    if (nativeMobile) {
        return (
            <Suspense fallback={<Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }} />}>
                <NativeDashboardLayout darkMode={darkMode} setDarkMode={setDarkMode} />
            </Suspense>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
            <CssBaseline />
            
            {/* AppBar (Barra superior) */}
            <ExecutiveAppBar
                handleDrawerToggle={handleDrawerToggle}
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                drawerWidth={drawerWidth}
                isSidebarCollapsed={isSidebarCollapsed}
                onToggleSidebarCollapse={handleSidebarCollapseToggle}
            />
            
            {/* Sidebar (Menú lateral) */}
            <ExecutiveSidebar
                mobileOpen={mobileOpen}
                handleDrawerToggle={handleDrawerToggle}
                drawerWidth={drawerWidth}
                collapsed={isSidebarCollapsed && isDesktop}
            />
            
            {/* Contenedor Principal */}
            <Box 
                component="main" 
                ref={mainRef}
                tabIndex={-1}
                aria-label="Main content"
                sx={{ 
                    flexGrow: 1, 
                    minWidth: 0,
                    maxWidth: '100%',
                    width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
                    minHeight: '100dvh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                    backgroundImage: theme.palette.mode === 'dark'
                        ? `linear-gradient(180deg, ${alpha('#1265f3', 0.07)} 0%, transparent 28%)`
                        : 'none',
                    transition: 'background-color 0.3s ease',
                }}
            >
                <Toolbar sx={{ mt: 'env(safe-area-inset-top, 0px)' }} />
                
                <Box 
                    sx={{ 
                        flexGrow: 1, 
                        minWidth: 0,
                        width: '100%', 
                        maxWidth: 'none',
                        mx: 'auto',
                        p: isChat ? 0 : { xs: 1.25, sm: 1.5, lg: 1.75 },
                        pb: isChat ? 0 : { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', md: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                        position: 'relative'
                    }}
                >
                    <Outlet />
                </Box>
                
                {!isChat && location.pathname !== '/dashboard' && <FloatingActionButton />}
            </Box>
            {!isChat && <MobileBottomNav />}
        </Box>
    );
}
